import { Alert, Box, Stack } from '@mui/material';
import {
  BindPageSubjectivity,
  SubjectivityDeadline,
  SubmissionStatus,
  useSubmissionSurplusLineQuery,
  useUpsertSubmissionSurplusLineMutation,
  useUpdateQuoteSubjectivityMutation,
} from '@converge/graphql';
import {
  Fragment,
  RefObject,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { UnderwritingSubjectivityListDetail } from './underwriting.subjectivity.list.detail';
import {
  SurplusLineHandle,
  UnderwritingSubmissionSurplusLine,
} from '../overview/underwriting.submission.surplusLine';
import { noCase } from 'change-case';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';
import { useConvergeSnackBar } from '../../../contexts';

const SURPLUS_LINE_SUBJECTIVITY_NAME = 'Surplus Lines Affidavit';

interface IUnderwritingSubjectivityList {
  subjectivities: BindPageSubjectivity[];
  deadline?: SubjectivityDeadline | SubjectivityDeadline[];
  submissionUuid: string;
  quoteUuid: string;
  setIsLoading?: (loading: boolean) => void;
  isLoading?: boolean;
  limitListToDeadline?: boolean;
  skipCheck?: boolean;
  setIsFetch: (value: SetStateAction<boolean>) => void;
  isFetch: boolean;
  surplusLineRef?: RefObject<SurplusLineHandle>;
  surplusLineFormEnabled?: boolean;
}

export const UnderwritingSubjectivityList = ({
  subjectivities,
  deadline,
  submissionUuid,
  quoteUuid,
  setIsLoading,
  isLoading,
  skipCheck,
  limitListToDeadline = false,
  setIsFetch,
  surplusLineRef,
  surplusLineFormEnabled = false,
}: IUnderwritingSubjectivityList) => {
  const {
    submission: { status },
  } = useUnderwritingSubmissionStateContext();

  const [, setHasUpdatedSubjectivities] = useState(false);
  const [isSurplusLineFormValid, setIsSurplusLineFormValid] = useState(false);
  const hasNotifiedUnsatisfied = useRef(false);
  const { upsertSubmissionSurplusLine } =
    useUpsertSubmissionSurplusLineMutation();
  const { updateQuoteSubjectivity } = useUpdateQuoteSubjectivityMutation();
  const { data: surplusLineData, refetch: refetchSurplusLine } =
    useSubmissionSurplusLineQuery(surplusLineRef ? submissionUuid : undefined);
  const { notify } = useConvergeSnackBar();
  const surplusLineInitialValues =
    surplusLineData?.submission?.surplusLinesLicenseeInfo ?? undefined;

  const handleSurplusLineValidityChange = useCallback((isValid: boolean) => {
    setIsSurplusLineFormValid(isValid);
  }, []);

  const handleSurplusLineFormChange = useCallback(
    async (subjectivity: BindPageSubjectivity) => {
      if (!subjectivity.quote?.isSatisfied) return;
      await updateQuoteSubjectivity({
        variables: {
          submissionUuid,
          details: {
            subjectivityUuid: subjectivity.uuid as string,
            isSatisfied: false,
          },
          quoteUuid,
          subjectivityUuid: subjectivity.uuid as string,
        },
      });
      if (!hasNotifiedUnsatisfied.current) {
        hasNotifiedUnsatisfied.current = true;
        notify({
          message:
            'You have modified the Surplus Lines Tax Filing information. Please check satisfied again before issuing the policy.',
          variant: 'error',
        });
      }
    },
    [submissionUuid, quoteUuid, updateQuoteSubjectivity, notify]
  );

  const filteredSubjectivities = useMemo(
    () =>
      subjectivities.filter((subjectivity) => {
        if (!deadline) {
          return true;
        }

        if (!subjectivity.quote?.deadline) {
          return true;
        }

        if (!limitListToDeadline) {
          return true;
        }

        return deadline instanceof Array
          ? subjectivity.quote?.deadline &&
              deadline.includes(subjectivity.quote?.deadline)
          : subjectivity.quote?.deadline === deadline;
      }),
    [subjectivities, deadline, limitListToDeadline]
  );

  return (
    <Fragment>
      <Stack mb={2}>
        <Box
          style={{ fontSize: '24px', fontWeight: '400', paddingBottom: '10px' }}
        >
          Subjectivities
        </Box>
        {filteredSubjectivities.length === 0 && (
          <Alert severity="info">
            No subjectivities due{' '}
            {noCase(
              deadline
                ? deadline instanceof Array
                  ? deadline
                      .map((text, index) => {
                        if (index) {
                          return `or ${text}`;
                        }
                        return text;
                      })
                      .join(', ')
                  : deadline
                : ''
            )}
            .{' '}
            {subjectivities.length > 0 && (
              <>
                Note that there {subjectivities.length === 1 ? 'is' : 'are'}{' '}
                {subjectivities.length} other{' '}
                {subjectivities.length === 1
                  ? 'subjectivity'
                  : 'subjectivities'}
                .
              </>
            )}
          </Alert>
        )}
        {filteredSubjectivities.map((subjectivity) => {
          const isSurplusLineSubjectivity = subjectivity.name?.includes(
            SURPLUS_LINE_SUBJECTIVITY_NAME
          );
          const subjectivityDeadline = subjectivity.quote?.deadline;
          const isPriorToBind =
            subjectivityDeadline === SubjectivityDeadline.PriorToBind;
          const isPriorToPolicy =
            subjectivityDeadline === SubjectivityDeadline.PriorToPolicy;
          // disable the form inputs when the deadline is PriorToPolicy on the bind page
          // (surplusLineFormEnabled=true means we're on the policy page — keep it active)
          const isPolicyIssued = status === SubmissionStatus.PolicyIssued;
          const isBound = status === SubmissionStatus.Bound;
          const surplusFormDisabled =
            !!surplusLineRef &&
            isSurplusLineSubjectivity &&
            ((isPriorToPolicy && !surplusLineFormEnabled) ||
              isPolicyIssued ||
              (isBound && isPriorToBind));
          // on the bind page (deadline prop === PriorToBind), disable Satisfied for any
          // subjectivity whose own deadline is not PriorToBind, use for disabled checkbox when prior to bond is not selected
          const notMatchingBindDeadline =
            deadline === SubjectivityDeadline.PriorToBind && !isPriorToBind;
          const shouldDisableSatisfied =
            notMatchingBindDeadline ||
            (!!surplusLineRef &&
              isSurplusLineSubjectivity &&
              (surplusFormDisabled ||
                (!isSurplusLineFormValid &&
                  (isPriorToBind ||
                    (isPriorToPolicy && surplusLineFormEnabled)))));

          return (
            <Fragment key={subjectivity.uuid}>
              <UnderwritingSubjectivityListDetail
                afterPolicy={subjectivity.afterPolicy}
                subjectivity={subjectivity}
                submissionUuid={submissionUuid}
                quoteUuid={quoteUuid}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                status={status}
                setIsFetch={setIsFetch}
                disableSatisfied={shouldDisableSatisfied}
                forceSatisfied={
                  !!surplusLineRef &&
                  isSurplusLineSubjectivity &&
                  isBound &&
                  isPriorToBind
                }
                attachedBelow={!!surplusLineRef && isSurplusLineSubjectivity}
                hideWaived={!!surplusLineRef && isSurplusLineSubjectivity}
                onSatisfied={
                  isSurplusLineSubjectivity && surplusLineRef
                    ? async () => {
                        hasNotifiedUnsatisfied.current = false;
                        await upsertSubmissionSurplusLine({
                          variables: {
                            details: {
                              uuid: submissionUuid,
                              surplusLinesLicenseeInfo:
                                surplusLineRef.current?.getValues(),
                            },
                          },
                        });
                        await refetchSurplusLine();
                      }
                    : undefined
                }
                onUpdate={() => {
                  setHasUpdatedSubjectivities(true);
                }}
              />
              {surplusLineRef && isSurplusLineSubjectivity && (
                <UnderwritingSubmissionSurplusLine
                  ref={surplusLineRef}
                  disabled={surplusFormDisabled}
                  clearOnDisable={isPriorToPolicy && !surplusLineFormEnabled}
                  attached
                  onValidityChange={handleSurplusLineValidityChange}
                  initialValues={surplusLineInitialValues}
                  onFormChange={() => handleSurplusLineFormChange(subjectivity)}
                />
              )}
            </Fragment>
          );
        })}
      </Stack>
    </Fragment>
  );
};

export default UnderwritingSubjectivityList;
